import type { CustomError } from "@whatslly/commons/utils";

export type MessageTypes = {
  [key: string]: (...args: any[]) => any | never;
};

export type MessageKeys<M extends MessageTypes> = {
  [key: string]: keyof M;
} & {
  ABORT_TRANSACTION: keyof M;
  RESPONSE: keyof M;
  ERROR: keyof M;
};

// eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
export type InputData<M extends MessageTypes, C extends keyof M> = M[C] extends Function ? Parameters<M[C]>[0] : never;
// eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
export type OutputData<M extends MessageTypes, C extends keyof M> = M[C] extends Function ? ReturnType<M[C]> : never;

export interface Request<M extends MessageTypes, C extends keyof M> {
  cmd: C;
  transactionId?: string;
  data: InputData<M, C>;
  chunk?: number;
  chunksDone?: boolean;
}

export type Response<M extends MessageTypes, C extends keyof M> = {
  cmd: MessageKeys<M>["RESPONSE"];
  data: OutputData<M, C>;
  transactionId?: string;
};
export type ErrorResponse<M extends MessageTypes> = {
  cmd: MessageKeys<M>["ERROR"];
  errorMessage: string;
  data: string;
  transactionId?: string;
};

interface RequestAndRespond<M extends MessageTypes, C extends keyof M, Metadata = any> {
  request: Request<M, C>;
  metadata?: Metadata;
  respond?: (response: Response<M, C> | ErrorResponse<M>) => void;
}

type CommandHandler<M extends MessageTypes, C extends keyof M, Metadata = any> = (
  data: InputData<M, C>,
  options: { signal: AbortSignal; transactionId?: string; metadata: Metadata }
) => Promise<OutputData<M, C>> | OutputData<M, C>;

const Subscribtions = Symbol.for("Subscribtions");
type UnsubscribeChain<M extends MessageTypes, Metadata = any> = (() => void) & {
  off: MessagingBackend<M, Metadata>["off"];
  on: MessagingBackend<M, Metadata>["on"];
  [Subscribtions]: [keyof M | Array<keyof M>, CommandHandler<M, keyof M>][];
};

function getUnsubscribeChain<M extends MessageTypes, C extends keyof M, Metadata = any>(
  backend: MessagingBackend<M, Metadata> | UnsubscribeChain<M, Metadata>,
  cmd: C | Array<C>,
  handler: CommandHandler<M, C>
): UnsubscribeChain<M, Metadata> {
  if (Subscribtions in backend) {
    backend[Subscribtions].push([cmd, handler]);
    return backend;
  }
  const off = () => {
    const subscribtions = (off as UnsubscribeChain<M, Metadata>)[Subscribtions];
    for (const [cmd, handler] of subscribtions) {
      if (Array.isArray(cmd)) {
        cmd.forEach((c) => backend.off(c, handler));
      } else {
        backend.off(cmd, handler);
      }
    }
  };
  Object.defineProperties(off, {
    [Subscribtions]: { value: [[cmd, handler]] },
    off: { value: backend.off.bind(backend) },
    on: { value: backend.on.bind(off) },
    handlers: { get: () => backend["handlers"] },
    abortControllers: { get: () => backend["abortControllers"] },
  });
  return off as UnsubscribeChain<M, Metadata>;
}

export class MessagingBackend<M extends MessageTypes, Metadata = any> {
  private readonly handlers = new Map<keyof M, CommandHandler<any, any>[]>();
  private readonly chunks = new Map<string, any>();
  private readonly abortControllers = new Map<string, AbortController>();
  constructor(
    private readonly messageKeys: MessageKeys<M>,
    private readonly prefix?: string
  ) {}

  public on<C extends keyof M>(cmd: C | Array<C>, handler: CommandHandler<M, C, Metadata>) {
    if (Array.isArray(cmd)) {
      cmd.forEach((c) => this.on(c, handler));
    } else if (!this.handlers.has(cmd)) {
      this.handlers.set(cmd, [handler]);
    } else {
      this.handlers.get(cmd)?.push(handler);
    }
    return getUnsubscribeChain(this, cmd, handler);
  }

  public off<C extends keyof M>(cmd: C, handler: CommandHandler<M, C, Metadata>) {
    const handlers = this.handlers.get(cmd);
    if (!handlers) {
      return this;
    }
    const index = handlers.indexOf(handler);
    if (index !== -1) {
      handlers.splice(index, 1);
    }
    return this;
  }

  public getHandler<O, C extends keyof M = keyof M>(
    transform?: (data: O) => RequestAndRespond<M, C, Metadata> | undefined | Promise<RequestAndRespond<M, C, Metadata> | undefined>
  ) {
    return async (data: O) => {
      const r = transform ? await transform(data) : { request: data as unknown as Request<M, C> };
      if (!r || !this.filterByPrefix(r.request)) {
        return;
      }
      const { respond, request, metadata } = r;
      this.handleMessage(request, respond, metadata);
    };
  }

  private filterByPrefix<C extends keyof M | string>({ cmd }: { cmd: C }) {
    return !this.prefix || (typeof cmd === "string" && cmd.startsWith(this.prefix));
  }

  private async handleMessage<C extends keyof M>(
    { cmd, data, transactionId, chunk, chunksDone }: Request<M, C>,
    respond?: (response: Response<M, C> | ErrorResponse<M>) => void,
    metadata: Metadata = undefined as Metadata
  ) {
    if (cmd === this.messageKeys.ABORT_TRANSACTION && transactionId) {
      this.abortControllers.get(transactionId)?.abort();
      this.abortControllers.delete(transactionId);
      return;
    }
    if (chunk !== undefined) {
      const acc = this.chunks.get(transactionId ?? "") ?? {};
      for (const [key, value] of Object.entries(data)) {
        if ((value as any).addToBuffer) {
          acc.addToBuffer ??= new Set();
          acc.addToBuffer.add(key);
          acc[key] ??= [];
          acc[key].push(Uint8Array.from((value as any).data));
        } else {
          acc[key] = value;
        }
      }
      if (chunksDone) {
        this.chunks.delete(transactionId ?? "");
        acc.addToBuffer?.forEach((key: string) => {
          acc[key] = typeof Buffer === "undefined" ? new Blob(acc[key]) : Buffer.concat(acc[key]);
        });
        data = acc;
      } else {
        this.chunks.set(transactionId ?? "", acc);
        return;
      }
    }

    const handlers = this.handlers.get(cmd)?.slice(0) as CommandHandler<M, C, Metadata>[];
    if (!handlers) {
      return;
    }
    for (const handler of handlers) {
      const controller = new AbortController();
      if (transactionId) {
        this.abortControllers.set(transactionId, controller);
      }
      try {
        const result = await handler(data, { signal: controller.signal, transactionId, metadata });
        if (transactionId && !controller.signal.aborted) {
          respond?.({ cmd: this.messageKeys.RESPONSE, data: result, transactionId });
        }
      } catch (err) {
        if (transactionId && !controller.signal.aborted) {
          const errorMessage = (err as Error).message;
          const errorData = (err as CustomError).data;
          respond?.({ cmd: this.messageKeys.ERROR, errorMessage, data: errorData ?? errorMessage, transactionId });
        }
      } finally {
        if (transactionId) {
          this.abortControllers.delete(transactionId);
        }
      }
    }
  }
}
